import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface SplitPanelPortalProps {
  children: React.ReactNode;
  /**
   * Portal target DOM id (default: 'split-panel-inner')
   */
  targetId?: string;
}

/**
 * Portal component for rendering children into split panel section.
 * Handles DOM lookup, cleanup, and error cases automatically.
 *
 * @example
 * ```tsx
 * <SplitPanelPortal>
 *   <RecordDetailPanel />
 * </SplitPanelPortal>
 * ```
 */
export default function SplitPanelPortal({
  children,
  targetId = 'split-panel-inner',
}: SplitPanelPortalProps) {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    // Try to find the target element
    const findTarget = () => {
      const element = document.getElementById(targetId);

      if (element) {
        setTargetElement(element);
        return true;
      }

      return false;
    };

    // Immediate attempt
    if (!findTarget()) {
      // Retry after a short delay in case the DOM is still being constructed
      timeoutRef.current = setTimeout(() => {
        if (!findTarget()) {
          console.warn(
            `[SplitPanelPortal] Target element #${targetId} not found. Panel content will not be rendered.`,
          );
        }
      }, 100);
    }

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [targetId]);

  // Don't render if target is not available
  if (!targetElement) {
    return null;
  }

  return createPortal(children, targetElement);
}
